import { useQuery } from '@tanstack/react-query'
import { ArrowDownLeft, ArrowUpRight, History } from 'lucide-react'
import type { ReactNode } from 'react'

import type { StockBalanceSummary } from '@aeris/contracts'
import { ZERO_QUANTITY, formatQuantity, parseQuantity } from '@aeris/inventory'
import { ZERO_MONEY, formatMoney, parseHumanAmount } from '@aeris/money'
import {
  MOVEMENT_KIND_LABELS,
  MOVEMENT_SOURCE_LABELS,
} from '@aeris/status-meta'
import {
  Badge,
  Dialog,
  DialogContent,
  EmptyState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchStockMovements } from '../lib/api.ts'

/**
 * The ledger, as a person reads it.
 *
 * This screen is the reason inventory is a ledger rather than a column. Every
 * line answers "why is the figure what it is?" — what happened, when, who did
 * it, and what the balance became. A mutable `stock` field has no answer to
 * that question, and it is the question every stock discrepancy starts with.
 *
 * Across every branch, not just the one being looked at: "where did this
 * product go?" is a question about the whole account, and quietly restricting
 * it to one branch would answer a different one while looking complete.
 */

// Os rótulos moram em `@aeris/status-meta` desde que o celular passou a
// mostrar o mesmo histórico (F311).
const KIND_LABELS = MOVEMENT_KIND_LABELS
const SOURCE_LABELS = MOVEMENT_SOURCE_LABELS

const dateFormat = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
})

const readable = (iso: string): string => dateFormat.format(new Date(iso))

/** Quantities are parsed with @aeris/money, never with `Number()`. */
const asQuantity = (value: string): string =>
  formatQuantity(parseQuantity(value) ?? ZERO_QUANTITY)

export type StockHistoryDialogProps = {
  balance: StockBalanceSummary
  onClose: () => void
}

export const StockHistoryDialog = ({
  balance,
  onClose,
}: StockHistoryDialogProps): ReactNode => {
  const query = useQuery({
    queryKey: ['stock-movements', balance.variantId],
    queryFn: ({ signal }) =>
      fetchStockMovements(
        { variantId: balance.variantId, page: 1, pageSize: 100 },
        signal
      ),
  })

  const items = query.data?.items ?? []

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title="Histórico de movimentações"
        description={`${balance.productName} — ${balance.sku}`}
        className="max-w-4xl"
      >
        <div className="flex flex-col gap-3">
          <p className="text-(length:--text-xs) text-(--text-secondary)">
            Saldo atual nesta filial:{' '}
            <strong className="font-(--font-mono) text-(--text-primary)">
              {asQuantity(balance.onHand)} {balance.unit}
            </strong>
            . O histórico abaixo cobre todas as filiais e nunca é apagado.
          </p>

          {query.isPending ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : query.isError ? (
            <EmptyState
              icon={<History aria-hidden="true" className="h-8 w-8" />}
              title="Não foi possível carregar o histórico"
              description="Verifique a conexão com o servidor e tente novamente."
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<History aria-hidden="true" className="h-8 w-8" />}
              title="Nenhuma movimentação"
              description="Este produto ainda não teve entradas nem saídas."
            />
          ) : (
            <TableContainer className="max-h-[60vh] overflow-auto">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Quando</TableHeaderCell>
                    <TableHeaderCell>Movimento</TableHeaderCell>
                    <TableHeaderCell>Filial</TableHeaderCell>
                    <TableHeaderCell numeric>Quantidade</TableHeaderCell>
                    <TableHeaderCell numeric>Saldo</TableHeaderCell>
                    <TableHeaderCell numeric>Custo unit.</TableHeaderCell>
                    <TableHeaderCell>Quem</TableHeaderCell>
                    <TableHeaderCell>Observação</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((movement) => (
                    <TableRow key={movement.id}>
                      <TableCell className="whitespace-nowrap text-(--text-secondary)">
                        {readable(movement.occurredAt)}
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1.5">
                          {movement.direction === 'in' ? (
                            <ArrowDownLeft
                              aria-hidden="true"
                              className="h-3.5 w-3.5 text-(--success)"
                            />
                          ) : (
                            <ArrowUpRight
                              aria-hidden="true"
                              className="h-3.5 w-3.5 text-(--danger)"
                            />
                          )}
                          {KIND_LABELS[movement.kind]}
                          {movement.sourceType === 'adjustment' ? (
                            <Badge tone="warning">
                              {SOURCE_LABELS[movement.sourceType]}
                            </Badge>
                          ) : null}
                        </span>
                      </TableCell>
                      <TableCell className="text-(--text-secondary)">
                        {movement.branchName}
                      </TableCell>
                      <TableCell numeric>
                        {/* The sign is rendered from `direction`; the stored
                            quantity is always positive. */}
                        {movement.direction === 'in' ? '+' : '−'}
                        {asQuantity(movement.quantity)}
                      </TableCell>
                      <TableCell numeric>
                        {asQuantity(movement.onHandAfter)}
                      </TableCell>
                      {/* O custo que entrou com a compra — é o que explica o
                          custo médio de hoje. Saída e ajuste não têm. */}
                      <TableCell numeric className="text-(--text-secondary)">
                        {movement.unitCost === null
                          ? '—'
                          : formatMoney(
                              parseHumanAmount(
                                movement.unitCost.replace('.', ','),
                                6
                              ) ?? ZERO_MONEY
                            )}
                      </TableCell>
                      <TableCell className="text-(--text-secondary)">
                        {movement.actorName ?? '—'}
                      </TableCell>
                      <TableCell className="text-(--text-secondary)">
                        {movement.note ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
